// IMPORTANT:
// - Wherever possible, use the bootstrap grid to create responsive pages.
// - When it it not possible, use these viewport vars to only render elements for current screen size
// - Because we usually have three mocks (mobile, tablet, and desktop),
// those three viewport sizes (isMobile, isTablet, isDesktop) should work for 99% of your needs.
// - Only use the size-specific variables (isXs, isSm, isMd, isLg, and isXl) when the device-sized variables won't work.

import React, { useState, useEffect } from 'react';
import { layoutHelpers } from '@/utils/helpers';

export const viewportContext = React.createContext({});

export const ViewportProvider = ({ children }) => {
  const [viewportWidth, setViewportWidth] = useState(0);

  useEffect(() => {
    let timer;
    const handleResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setViewportWidth(window.innerWidth);
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    setViewportWidth(window.innerWidth); // set immediately on mount, no debounce
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, []);

  const isMobile = viewportWidth > 0 && viewportWidth <= layoutHelpers.mobileMax; // = bootstrap extra small and small
  const isTablet = viewportWidth >= layoutHelpers.tabletMin && viewportWidth <= layoutHelpers.tabletMax; // = bootstrap medium
  const isMobileOrTablet = viewportWidth > 0 && viewportWidth <= layoutHelpers.tabletMax; // = bootstrap extra small, small, and medium
  const isDesktop = viewportWidth >= layoutHelpers.desktopMin; // = bootstrap large

  const isXs = viewportWidth > 0 && viewportWidth <= layoutHelpers.xsMax; // = bootstrap extra small and below
  const isXl = viewportWidth >= layoutHelpers.xlMin; // = bootstrap extra large, extra extra large, and above

  // IMPORTANT: These variables have been deprecated and are being phased out.
  // Please use the variables above!
  const unusedIsXSPhone = viewportWidth >= layoutHelpers.unusedXSPhoneBreakPoint && viewportWidth < layoutHelpers.unusedPhoneBreakPoint;
  const unusedIsPhone = viewportWidth >= layoutHelpers.unusedPhoneBreakPoint && viewportWidth < layoutHelpers.unusedMobileBreakPoint;
  const unusedIsTablet = viewportWidth >= layoutHelpers.unusedMobileBreakPoint && viewportWidth < layoutHelpers.unusedTabletBreakPoint;
  const unusedIsXSDesktop = viewportWidth >= layoutHelpers.unusedTabletBreakPoint && viewportWidth < layoutHelpers.unusedDeskTopBreakPoint;
  const unusedIsDesktop = viewportWidth >= layoutHelpers.unusedDeskTopBreakPoint;
  const unusedIsMobile = viewportWidth >= layoutHelpers.unusedXSPhoneBreakPoint && viewportWidth < layoutHelpers.unusedMobileBreakPoint;

  return (
    <viewportContext.Provider
      value={{
        viewportWidth,
        isMobile,
        isTablet,
        isMobileOrTablet,
        isDesktop,
        isXs,
        isXl,
        unusedIsDesktop,
        unusedIsXSDesktop,
        unusedIsTablet,
        unusedIsPhone,
        unusedIsXSPhone,
        unusedIsMobile
      }}
    >
      {children}
    </viewportContext.Provider>
  );
};

export const ViewportConsumer = viewportContext.Consumer;
